<script setup lang="ts">
  /**
   * HeroShowcase — large hero carousel ("1 of N", prev/next, pause,
   * autoplay) with a row of secondary story cards beneath it. On mobile
   * the carousel becomes a horizontal swipe track with card peek, like
   * Yahoo's.
   *
   * Story links open in a new tab and leave the portal untouched, the same
   * way the platform's own content navigation does (useRedirectActions
   * navigates with `{ open: { target: '_blank' } }`). rel stays at
   * `noopener` rather than adding `noreferrer`, because these are our own
   * same-origin article pages and they should still see the referrer.
   */
  import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    resolveComponent,
    watch,
  } from 'vue'
  import { useMediaQuery } from '@vueuse/core'
  import { resolveRegisteredComponent } from '../../registries/componentRegistry'
  import { minWidthQuery } from '../../../layout-system/screens'
  import type { HeroShowcaseProps, ShowcaseArticle } from './types'

  const props = withDefaults(defineProps<HeroShowcaseProps>(), {
    articles: () => [],
    subArticles: () => [],
  })

  const NuxtLink = resolveComponent('NuxtLink')

  // Smart ad components contributed at runtime by the hosting app (common
  // registers PortalFeedAd and AdSlot at boot). Undefined in standalone
  // lego-land, where ad slides drop out of the rotation entirely.
  // PortalFeedAd renders the native dplxapi creative (mobile slides);
  // AdSlot renders the classic widget slots (desktop slides, sub-row ad).
  const PortalFeedAd = resolveRegisteredComponent('PortalFeedAd')
  const AdSlot = resolveRegisteredComponent('AdSlot')

  const isAdSlide = (article: ShowcaseArticle) =>
    Boolean(article.adPlacement || article.adSlot)

  /**
   * Slides actually shown. An ad slide needs the renderer its own fields
   * point at: the widget slot needs AdSlot, the native creative needs
   * PortalFeedAd. Either one keeps the slide in the rotation, and each
   * render branch below still checks its own component.
   */
  const canRenderAdSlide = (article: ShowcaseArticle) =>
    Boolean(article.adPlacement && PortalFeedAd) ||
    Boolean(article.adSlot && AdSlot)
  const slides = computed(() =>
    props.articles.filter(
      (article) => !isAdSlide(article) || canRenderAdSlide(article)
    )
  )

  const adSlides = computed(() => slides.value.filter(isAdSlide))

  const activeIndex = ref(0)
  const paused = ref(false)
  const hovered = ref(false)

  /**
   * The mobile track and the desktop carousel are both in the DOM at every
   * width (the split is CSS-only), so without a gate each ad slot would
   * mount PortalFeedAd twice and fetch two creatives. Ads never SSR, so the
   * creatives mount after hydration on exactly one branch: server and
   * hydration render agree (neither mounts ads), then the media query picks
   * the branch. The wrappers keep their pulse skeletons either way.
   */
  // A media query rather than the platform's useAdFunctions().isMobile:
  // lego-land cannot import from common (PortalFeedAd itself only arrives
  // here through the registry for the same reason). The query comes from
  // the layout system's SCREENS, so it flips exactly where this
  // component's `tablet:` classes do.
  const isMounted = ref(false)
  const isDesktop = useMediaQuery(minWidthQuery('tablet'))
  const mountMobileAds = computed(() => isMounted.value && !isDesktop.value)
  const mountDesktopAds = computed(() => isMounted.value && isDesktop.value)

  /**
   * The rotation is rebuilt whenever the article pool changes, for instance on
   * a category switch, and this component keeps its instance across that. A
   * shorter rotation would otherwise leave activeIndex past the end and blank
   * the hero, so the index follows the slides back into range.
   */
  watch(
    () => slides.value.length,
    (count) => {
      if (activeIndex.value >= count) activeIndex.value = 0
    }
  )

  const activeSlide = computed(
    () => slides.value[Math.min(activeIndex.value, slides.value.length - 1)]
  )

  function next() {
    if (!slides.value.length) return
    activeIndex.value = (activeIndex.value + 1) % slides.value.length
  }

  function prev() {
    if (!slides.value.length) return
    activeIndex.value =
      (activeIndex.value - 1 + slides.value.length) % slides.value.length
  }

  // Auto-advance like Yahoo's hero; the pause button and hover both halt it
  const AUTOPLAY_MS = 6000
  let autoplayTimer: ReturnType<typeof setInterval> | null = null
  onMounted(() => {
    isMounted.value = true
    // Respect reduced motion: the hero stays on its first slide and only
    // moves when the reader asks it to
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    autoplayTimer = setInterval(() => {
      if (!paused.value && !hovered.value && slides.value.length > 1) {
        next()
      }
    }, AUTOPLAY_MS)
  })
  onBeforeUnmount(() => {
    if (autoplayTimer) {
      clearInterval(autoplayTimer)
    }
  })
</script>

<template>
  <!-- Hero + sub-cards live inside one module card. Measured on
       yahoo.com: a 584px card at 12px radius with 16px padding, holding a
       552px media block that is itself rounded to 12px. -->
  <div class="rounded-lg bg-surface-primary p-4" data-portal-module="hero">
    <!-- Mobile: horizontal swipe track with card peek, like Yahoo -->
    <div
      v-if="slides.length"
      class="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 [-ms-overflow-style:none] [scrollbar-width:none] tablet:hidden [&::-webkit-scrollbar]:hidden"
    >
      <article
        v-for="(article, index) in slides"
        :key="article.id"
        class="group relative aspect-[4/3] w-[86%] shrink-0 snap-center overflow-hidden rounded-md"
      >
        <!-- Ad slide: the same dplxapi native creative the feed ad rows
             use, centered so the card keeps the story cards' shape -->
        <div
          v-if="isAdSlide(article)"
          class="absolute inset-0 [&:not(:has(a))]:animate-pulse"
        >
          <component
            :is="PortalFeedAd"
            v-if="mountMobileAds && article.adPlacement && PortalFeedAd"
            :placement="article.adPlacement"
            view="hero"
          />
        </div>

        <template v-else>
          <img
            :src="article.imageUrl"
            alt=""
            class="absolute inset-0 h-full w-full object-cover"
            :loading="index === 0 ? 'eager' : 'lazy'"
          />
          <div
            class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent"
          />
          <!-- Attribution and headline share one bottom anchored column, so
               a three line headline pushes the publisher row up instead of
               growing into it -->
          <div class="absolute inset-x-4 bottom-4 flex flex-col gap-2">
            <div
              v-if="article.sourceName"
              class="flex items-center gap-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]"
            >
              <img
                v-if="article.sourceIconUrl"
                :src="article.sourceIconUrl"
                alt=""
                class="h-4 w-4 rounded-full"
              />
              <span class="text-[13px] font-semibold text-white">
                {{ article.sourceName }}
              </span>
            </div>
            <h2
              class="line-clamp-3 text-xl font-bold leading-[1.15] text-white"
            >
              {{ article.title }}
            </h2>
          </div>
          <NuxtLink
            v-if="article.articleUrl"
            :to="article.articleUrl"
            target="_blank"
            rel="noopener"
            :aria-label="article.title"
            class="absolute inset-0"
          />
        </template>

        <span
          class="absolute right-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-xs text-white/80 backdrop-blur"
        >
          <span class="font-bold text-white">{{ index + 1 }}</span>
          of {{ slides.length }}
        </span>
      </article>
    </div>

    <!-- Desktop: single-slide carousel with controls. The frame is a fixed
         612x304 box — the Outbrain slider unit's standard zone (the same
         styleMixins 304_612 the classic carousel serves it in). The unit
         is never scaled or restyled; story slides conform to it instead,
         so every slide is identical and the rotation never jumps. On
         widths where the card is wider than the unit, the frame centers. -->
    <article
      v-if="activeSlide"
      class="group relative mx-auto hidden h-[304px] w-full max-w-[612px] overflow-hidden rounded-lg tablet:block"
      :class="{ 'cursor-pointer': !isAdSlide(activeSlide) }"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
      @focusin="hovered = true"
      @focusout="hovered = false"
    >
      <!-- Ad slides. All of them stay mounted and toggle with v-show —
           rotating back to a slide reuses its creative instead of
           refetching it and counting a second impression. Slides with an
           adSlot render the classic Outbrain slider widget (SUP-668),
           centered in the hero frame; its lazyRender waits for actual
           visibility, so a hidden slide requests nothing until the
           rotation first reaches it. Slides without one keep the dplxapi
           native creative. -->
      <div
        v-for="slide in adSlides"
        v-show="slide.id === activeSlide.id"
        :key="slide.id"
        class="absolute inset-0 [&:not(:has(a,iframe))]:animate-pulse"
      >
        <!-- The widget renders at its native size, exactly as the classic
             ShiftNews carousel serves the same unit (612-wide full-bleed
             rec, per SUP-668 the sizes must match); only the Ad chip is
             added, matching the mobile ad slides' disclosure -->
        <template v-if="slide.adSlot && AdSlot">
          <component
            :is="AdSlot"
            v-if="mountDesktopAds"
            class="absolute inset-0"
            :placement="slide.adSlot.placement"
            :ad-type="slide.adSlot.adType"
            :width="slide.adSlot.width"
            :height="slide.adSlot.height"
            :lazy-render="true"
          />
          <span
            class="absolute left-3 top-3 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-bold uppercase leading-4 tracking-wide text-white backdrop-blur"
          >
            Ad
          </span>
        </template>
        <component
          :is="PortalFeedAd"
          v-else-if="mountDesktopAds && slide.adPlacement && PortalFeedAd"
          :placement="slide.adPlacement"
          view="hero"
        />
      </div>

      <template v-if="!isAdSlide(activeSlide)">
        <Transition
          mode="out-in"
          enter-active-class="transition-opacity duration-300"
          enter-from-class="opacity-0"
          leave-active-class="transition-opacity duration-200"
          leave-to-class="opacity-0"
        >
          <img
            :key="activeSlide.id"
            :src="activeSlide.imageUrl"
            alt=""
            class="absolute inset-0 h-full w-full object-cover"
          />
        </Transition>

        <!-- Bottom gradient -->
        <div
          class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent"
        />

        <!-- Attribution and headline share one bottom anchored column, so a
             three line headline pushes the publisher row up rather than
             growing into it. The right inset clears the prev and next
             controls. -->
        <div
          class="absolute bottom-5 left-5 right-16 flex flex-col gap-2 tablet:right-28"
        >
          <div
            v-if="activeSlide.sourceName"
            class="flex items-center gap-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]"
          >
            <img
              v-if="activeSlide.sourceIconUrl"
              :src="activeSlide.sourceIconUrl"
              alt=""
              class="h-4 w-4 rounded-full"
            />
            <span class="text-[13px] font-semibold text-white">
              {{ activeSlide.sourceName }}
            </span>
          </div>

          <h2
            class="line-clamp-3 text-2xl font-bold leading-[1.15] text-white group-hover:underline tablet:text-[32px]"
          >
            {{ activeSlide.title }}
          </h2>
        </div>

        <!-- Stretched link: whole hero navigates; controls sit above (z-10) -->
        <NuxtLink
          v-if="activeSlide.articleUrl"
          :to="activeSlide.articleUrl"
          target="_blank"
          rel="noopener"
          :aria-label="activeSlide.title"
          class="absolute inset-0"
        />
      </template>

      <!-- Slide counter + pause -->
      <div class="absolute right-4 top-4 z-10 flex items-center gap-2">
        <span
          class="rounded-full bg-black/60 px-3 py-1.5 text-xs text-white/80 backdrop-blur"
        >
          <span class="font-bold text-white">{{ activeIndex + 1 }}</span>
          of {{ slides.length }}
        </span>
        <button
          type="button"
          :aria-label="paused ? 'Resume slideshow' : 'Pause slideshow'"
          class="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 backdrop-blur transition-colors hover:bg-black/80"
          @click.stop="paused = !paused"
        >
          <UIcon
            :name="
              paused ? 'i-heroicons-play-solid' : 'i-heroicons-pause-solid'
            "
            class="h-3.5 w-3.5 text-white"
          />
        </button>
      </div>

      <!-- Prev / next -->
      <div class="absolute bottom-5 right-4 z-10 flex gap-2">
        <button
          type="button"
          aria-label="Previous story"
          class="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/60 backdrop-blur transition-colors hover:bg-black/80"
          @click.stop="prev"
        >
          <UIcon name="i-heroicons-chevron-left" class="h-5 w-5 text-white" />
        </button>
        <button
          type="button"
          aria-label="Next story"
          class="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/60 backdrop-blur transition-colors hover:bg-black/80"
          @click.stop="next"
        >
          <UIcon name="i-heroicons-chevron-right" class="h-5 w-5 text-white" />
        </button>
      </div>
    </article>

    <!-- Secondary row: three equal tiles like Yahoo's. With a
         subAdPlacement (SUP-668) the middle tile is an API-served native
         rendered by PortalFeedAd in the same card dress as the stories —
         the API is used here precisely because no standard widget fits a
         tile this size. Below tablet the story cards stack and the ad
         tile stays out (PortalFeedAd's lazy fetch never fires while its
         cell is display:none). -->
    <div
      v-if="subArticles.length"
      class="mt-4 grid grid-cols-1 gap-4 tablet:grid-cols-3"
      data-portal-module="hero-sub"
    >
      <!-- Cards stretch to the tallest headline and push the publisher row
           to their bottom, so the source rows line up like Yahoo's -->
      <template v-for="(article, index) in subArticles" :key="article.id">
        <component
          :is="article.articleUrl ? NuxtLink : 'article'"
          :to="article.articleUrl"
          :target="article.articleUrl ? '_blank' : undefined"
          :rel="article.articleUrl ? 'noopener' : undefined"
          class="group flex h-full cursor-pointer flex-col"
        >
          <img
            :src="article.imageUrl"
            alt=""
            class="aspect-[16/9] w-full rounded-md object-cover"
            loading="lazy"
          />
          <p
            class="mt-3 line-clamp-3 text-[15px] font-semibold leading-snug text-content-primary group-hover:underline"
          >
            {{ article.title }}
          </p>
          <p
            v-if="article.sourceName"
            class="mt-auto flex items-center gap-2 pt-2.5 text-[13px] text-content-secondary"
          >
            <img
              v-if="article.sourceIconUrl"
              :src="article.sourceIconUrl"
              alt=""
              class="h-3.5 w-3.5 rounded-sm"
            />
            {{ article.sourceName }}
          </p>
        </component>

        <!-- Middle tile: the API native, after the first story card -->
        <div
          v-if="index === 0 && subAdPlacement && PortalFeedAd"
          class="hidden tablet:block"
        >
          <component
            :is="PortalFeedAd"
            :placement="subAdPlacement"
            view="tile"
          />
        </div>
      </template>
    </div>
  </div>
</template>
